import Tree, {
  moveItemOnTree,
  mutateTree,
  RenderItemParams,
  TreeData,
  TreeDestinationPosition,
  TreeSourcePosition,
} from "@atlaskit/tree";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import PanToolIcon from "@mui/icons-material/PanTool";
import PersonIcon from "@mui/icons-material/Person";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  InputLabel,
  OutlinedInput,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import moment from "moment";
import React, {
  createRef,
  ElementRef,
  forwardRef,
  RefObject,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { HuePicker } from "react-color";
import Modal, { CommonModalProps } from "..";
import { cacheService, orderService, planningUserTaskService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { DbObjectCreateOrUpdateInfos } from "../../../Core/Api/Types/db-object-create-or-update-infos";
import {
  DbOrderManualTaskPlanningInfos,
  ManualTaskPlanningEventInfos,
} from "../../../Core/Api/Types/db-order-manual-task-planning-infos";
import { DbOrderManualTaskPlanningResourceInfos } from "../../../Core/Api/Types/db-order-manual-task-planning-resource-infos";
import { DbUserBasicInfos } from "../../../Core/Api/Types/db-user-basic-infos";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { DeleteForm, GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { UserHasAcl } from "../../../Utils/helpers";
import { hexToRgbString, isColorDark } from "../../../Utils/Helpers/cssUtils";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import FormInputDate from "../../Forms/FormInputDate/form-input-date";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { useTranslation } from "react-i18next";

// #region Utils
function hhmmToTimeSpan(value: string | null): string | null {
  if (!value || value.isEmpty()) return null;

  const digits = value.replace(/\D/g, "");
  if (digits.length !== 4) return null;

  const totalHours = parseInt(digits.substring(0, 2));
  const minutes = parseInt(digits.substring(2));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours - days * 24;

  if (days > 0) return `${days}.${hours}:${minutes}:00`;
  else return `${hours}:${minutes}:00`;
}

function timeSpanToHhmm(value: string | null): string | null {
  if (!value || value.isEmpty()) return null;

  const splittedValue = value.split(":");

  if (splittedValue.length !== 3) return null;
  let hours = 0;
  let minutes = 0;

  if (splittedValue[0].includes(".")) {
    const splittedDaysHours = splittedValue[0].split(".");
    const days = parseInt(splittedDaysHours[0]);
    hours = parseInt(splittedDaysHours[1]);
    hours += days * 24;
  } else hours = parseInt(splittedValue[0]);

  minutes = parseInt(splittedValue[1]);

  let hoursString = hours < 10 ? `0${hours}` : hours.toString();
  let minutesString = minutes < 10 ? `0${minutes}` : minutes.toString();

  return `${hoursString}:${minutesString}`;
}
// #endregion Utils

export type ModalEditTaskElementProps = {
  task: DbOrderManualTaskPlanningInfos;
  users: DbUserBasicInfos[];
  resources: DbOrderManualTaskPlanningResourceInfos[];
  eventToOpenGuid?: string;
};

export type ModalEditTaskProps = CommonModalProps<ModalEditTaskElementProps>;

const ModalEditTask = React.forwardRef<any, ModalEditTaskProps>((props, ref) => {
  const { t } = useTranslation();
  const { task, users, resources, eventToOpenGuid, closeModal } = props;

  const dispatch = useAppDispatch();
  const enqueueSnackbar = useCustomSnackbar();

  const userHasRightToUpdate = UserHasAcl(AclTypes.aclPlanningUserTaskUpdate);
  const formId = useMemo(() => "modal-edit-task", []);
  const formSectionId = useMemo(() => "task-name-section", []);

  const [showLoader, setShowLoader] = useState(false);
  const [name, setName] = useState(task.name || "");
  const [orderColor, setOrderColor] = useState(`#${task.orderItemColor}` || undefined);
  const [treeData, setTreeData] = useState<TreeData>({
    rootId: 0,
    items: {
      0: {
        children: [],
        id: 0,
        hasChildren: true,
        isExpanded: false,
        isChildrenLoading: false,
        data: {
          name: "__root__",
        },
      },
    },
  });
  const [eventCardsRefs, setEventCardsRefs] = useState<Record<string, RefObject<EventCardRef | null>>>({});

  const formIsValid = useAppSelector(GetFormIsValid(formId));

  const handleCreateNewEvent = () => {
    // Create event
    const newEvent: ManualTaskPlanningEventInfos = {
      guid: "00000000-0000-0000-0000-000000000000", // Guid.Empty
      allDay: false,
      assignInfos: null,
      claimInfos: null,
      name: task.name,
      resourceIds: null,
      dueDateElapsed: false,
      spentTime: null,
      comment: null,
    };

    // Push to tree
    const newTreeData = { ...treeData };
    newTreeData.items[newEvent.guid] = {
      children: [],
      id: newEvent.guid,
      hasChildren: false,
      isExpanded: false,
      isChildrenLoading: false,
      data: newEvent,
    };

    setTreeData(
      mutateTree(newTreeData, 0, {
        isExpanded: true,
        hasChildren: true,
        children: [...newTreeData.items[0].children, newEvent.guid],
      })
    );
    // Set refs
    const newRefs: {
      [x: string]: React.RefObject<EventCardHandle | null>;
    } = { ...eventCardsRefs };
    newRefs[newEvent.guid] = createRef();
    setEventCardsRefs(newRefs);
  };

  const handleDeleteEvent = (guid: string) => {
    const newTreeData = {
      ...treeData,
    };

    // 2 cause root (0) + event (guid)
    if (Object.keys(newTreeData.items).length === 2) {
      enqueueSnackbar(t("alert:warning.eventRequired"), "warning");
      return;
    }

    if (newTreeData.items[guid] !== undefined) {
      delete newTreeData.items[guid];
      setTreeData(
        mutateTree(newTreeData, 0, {
          isExpanded: true,
          hasChildren: newTreeData.items[0].children.length > 0,
          children: newTreeData.items[0].children.filter((x) => x !== guid),
        })
      );
    }
  };

  const onDragEnd = (source: TreeSourcePosition, destination?: TreeDestinationPosition) => {
    if (!destination) {
      return;
    }

    setTreeData(moveItemOnTree(treeData, source, destination));
  };

  const renderItem = (renderItemParams: RenderItemParams) => {
    const event = renderItemParams.item.data as ManualTaskPlanningEventInfos;

    return (
      <div
        ref={renderItemParams.provided.innerRef}
        {...renderItemParams.provided.draggableProps}
        {...renderItemParams.provided.dragHandleProps}
      >
        <Divider sx={{ mt: 2 }} variant="middle" />
        <EventCard
          ref={eventCardsRefs[event.guid] as any}
          event={event}
          formId={formId}
          users={users}
          resources={resources}
          isOpen={!eventToOpenGuid || eventToOpenGuid === event.guid}
          onDeleteEvent={handleDeleteEvent}
        />
      </div>
    );
  };

  const handleConfirmClick = () => {
    setShowLoader(true);
    const newEvents: ManualTaskPlanningEventInfos[] = [];
    treeData.items[0].children.forEach((key) => {
      if (eventCardsRefs[key]?.current) newEvents.push(eventCardsRefs[key].current!.getEventUpdated());
    });

    planningUserTaskService
      .UpdateTaskById({
        taskId: task.id,
        name: name,
        comment: null,
        status: null,
        events: newEvents,
      })
      .then((response) => {
        if (response.success) {
          enqueueSnackbar(t("alert:success.updatedTask"), "success");
          closeModal && closeModal();
        }
        enqueueSnackbar(t("alert:error.errorUpdatingTask", { message: response.message }), "error");
      })
      .finally(() => setShowLoader(false));

    const newOrderColor = orderColor?.substring(1);
    if (newOrderColor && newOrderColor !== task.orderItemColor && task.orderItemId)
      orderService.AssignColorToOrderItem(task.orderItemId, newOrderColor);
  };

  const handleCancelClick = () => {
    closeModal();
  };

  useEffect(() => {
    const newRefs: Record<string, RefObject<EventCardRef | null>> = {};
    const newTreeData: TreeData = {
      rootId: 0,
      items: {
        0: {
          children: [],
          id: 0,
          hasChildren: true,
          isExpanded: false,
          isChildrenLoading: false,
          data: {
            name: "__root__",
          },
        },
      },
    };
    task.events?.forEach((event) => {
      newTreeData.items[event.guid] = {
        id: event.guid,
        children: [],
        hasChildren: false,
        isChildrenLoading: false,
        isExpanded: false,
        data: event,
      };
      newTreeData.items[0].children.push(event.guid);

      newRefs[event.guid] = createRef();
    });

    setTreeData(
      mutateTree(newTreeData, 0, { isExpanded: true, hasChildren: true, children: newTreeData.items[0].children })
    );
    setEventCardsRefs(newRefs);

    return () => {
      dispatch(DeleteForm({ formId: formId }));
    };
  }, []);

  return (
    <Modal
      title={
        <Typography variant="h5" color={isColorDark(orderColor) ? "text.primary" : "#333333"}>
          {t("modal:title.editTaskWithId", { id: task.id })}
        </Typography>
      }
      reference={ref}
      closeModal={handleCancelClick}
      width={700}
      headerBackgroundColor={orderColor ? `rgba(${hexToRgbString(orderColor)}, 0.55)` : undefined}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button color="success" variant="outlined" onClick={handleConfirmClick} disabled={!formIsValid || showLoader}>
            {showLoader ? <CircularProgress size={20} thickness={5} color="success" /> : t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <FormInputText
          label={t("field:label.taskName")}
          validationProps={{
            formId: formId,
            formSectionId: formSectionId,
            fieldId: `${formSectionId}-task-name`,
            required: true,
          }}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Box sx={{ pb: 1 }}>
          <InputLabel sx={{ pb: 1 }}>{t("field:label.orderItemColor")}</InputLabel>
          <HuePicker
            color={orderColor}
            onChange={(color, _) => userHasRightToUpdate && setOrderColor(color.hex)}
            width="100%"
          />
        </Box>
        {userHasRightToUpdate && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            //sx={{ ml: 1 }}
            onClick={handleCreateNewEvent}
          >
            {t("button:newEvent")}
          </Button>
        )}
        <Stack spacing={1}>
          <Tree tree={treeData} renderItem={renderItem} onDragEnd={onDragEnd} isDragEnabled isNestingEnabled={false} />
        </Stack>
      </Stack>
    </Modal>
  );
});

export default ModalEditTask;

interface EventCardProps {
  event: ManualTaskPlanningEventInfos;
  formId: string;
  users: DbUserBasicInfos[];
  resources: DbOrderManualTaskPlanningResourceInfos[];
  isOpen?: boolean;
  onDeleteEvent: (guid: string) => void;
}

interface EventCardHandle {
  getEventUpdated: () => ManualTaskPlanningEventInfos;
}

type EventCardRef = ElementRef<typeof EventCard>;

const EventCard = forwardRef<EventCardHandle, EventCardProps>((props, ref) => {
  const { t } = useTranslation();
  const { event, formId, users, resources, isOpen = true, onDeleteEvent } = props;

  const loggedUser = cacheService.GetUserInfo();

  const formSectionId = useMemo(() => `event-card-${event.guid}`, [event]);

  const [name, setName] = useState(event.name);
  const [spentTime, setSpentTime] = useState<string | null>(timeSpanToHhmm(event.spentTime));
  const [allDay, setAllDay] = useState(event.allDay);
  const [assignedTo, setAssignedTo] = useState(users.find((u) => u.id === event.assignInfos?.userId) || null);
  const [dateIn, setDateIn] = useState<Date | null>(event.assignInfos?.date || null);
  const [dateOut, setDateOut] = useState<Date | null>(event.assignInfos?.endDate || null);
  const [claimedBy, setClaimedBy] = useState(users.find((u) => u.id === event.claimInfos?.userId) || null);
  const [comment, setComment] = useState(event.comment);
  const [selectedResources, setSelectedResources] = useState(
    resources.filter((r) => event.resourceIds?.includes(r.id))
  );

  const handleAssignToMe = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!loggedUser) return;

    const user = users.find((u) => u.id === loggedUser.id);
    if (!user) return;

    setAssignedTo(user);
  };

  const handleClaim = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!claimedBy) {
      if (!loggedUser) return;

      const user = users.find((u) => u.id === loggedUser.id);
      if (!user) return;

      setClaimedBy(user);
    } else setClaimedBy(null);
  };

  const handleDeleteEvent = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();

    onDeleteEvent(event.guid);
  };

  const handleGetEventUpdated = () => {
    const eventUpdated = {
      ...event,
      name: name,
      spentTime: hhmmToTimeSpan(spentTime),
      allDay: allDay,
      comment: comment != null && !comment.isEmpty() ? comment : null,
    } as ManualTaskPlanningEventInfos;

    if (assignedTo || dateIn || dateOut)
      eventUpdated.assignInfos = new DbObjectCreateOrUpdateInfos({
        id: -500,
        userId: assignedTo?.id,
        date: dateIn || undefined,
        endDate: dateOut,
      });
    else eventUpdated.assignInfos = null;

    if (claimedBy)
      eventUpdated.claimInfos = new DbObjectCreateOrUpdateInfos({
        id: -500,
        userId: claimedBy.id,
      });
    else eventUpdated.claimInfos = null;

    if (selectedResources.length) eventUpdated.resourceIds = selectedResources.map((resource) => resource.id);
    else eventUpdated.resourceIds = null;

    return eventUpdated;
  };

  const [trick, setTrick] = useState(false);

  const handleSetDateIn = (value: Date) => {
    setDateIn(value);

    if ((!dateOut || !trick) && moment(value.toString()).minute()) {
      if (dateOut) setTrick(true);
      setDateOut(moment(value.toString()).add(1, "h").toDate());
    }
  };

  useImperativeHandle(ref, () => ({
    getEventUpdated: handleGetEventUpdated,
  }));

  return (
    <Accordion defaultExpanded={isOpen}>
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon />}
        sx={{ ".MuiAccordionSummary-content": { justifyContent: "space-between", mr: 1 } }}
      >
        <Typography variant="subtitle2" component="h3" fontWeight={600}>
          {name} {assignedTo ? `- ${assignedTo.name}` : ""}{" "}
          {dateOut ? `- ${moment(dateOut.toString()).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}` : ""}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title={t("tooltip:title.assignToMe")}>
            <Button
              size="small"
              sx={{ p: 0.5 }}
              color={assignedTo ? "success" : "inherit"}
              onClick={(event) => handleAssignToMe(event)}
            >
              <PersonIcon />
            </Button>
          </Tooltip>
          <Tooltip title={t("tooltip:title.claimTask")}>
            <Button
              size="small"
              sx={{ p: 0.5 }}
              color={claimedBy !== null && claimedBy.id === loggedUser?.id ? "success" : "inherit"}
              onClick={(event) => handleClaim(event)}
            >
              <PanToolIcon />
            </Button>
          </Tooltip>
          <Tooltip title={t("tooltip:title.deleteEvent")}>
            <Button size="small" sx={{ p: 0.5 }} onClick={(event) => handleDeleteEvent(event)}>
              <DeleteOutlineOutlinedIcon color="error" />
            </Button>
          </Tooltip>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1}>
            <FormInputText
              label={t("field:label.eventName")}
              stackProps={{ sx: { flex: 1 } }}
              validationProps={{
                required: true,
                formId: formId,
                formSectionId: formSectionId,
                fieldId: `${formSectionId}-event-name`,
              }}
              value={name || ""}
              onChange={(event) => setName(event.target.value)}
            />
            <SpentTimeInput spentTime={spentTime} setSpendTime={setSpentTime} />
          </Stack>
          <Autocomplete
            sx={{ flex: 1 }}
            popupIcon={<ExpandMoreRoundedIcon />}
            options={users}
            getOptionLabel={(option) => `${option.name} - ${option.login}`}
            value={assignedTo}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(event, value) => setAssignedTo(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("field:label.assignedTo")}
                placeholder={t("field:placeholder.selectUser")}
              />
            )}
          />
          <Stack direction="row" spacing={1}>
            <FormInputDate
              label={t("field:label.startDateAndTime")}
              hideReset
              validationProps={{
                formId: formId,
                formSectionId: formSectionId,
                fieldId: `${formSectionId}-event-date-in`,
                required: assignedTo !== null && !allDay,
                before: dateOut || undefined,
              }}
              format={GlobalConst.MOMENT_DATE_FORMAT_FULL}
              value={dayjs(dateIn)}
              onChange={handleSetDateIn}
              disabled={allDay}
            />
            <FormInputDate
              label={t("field:label.endDateAndTime")}
              hideReset
              validationProps={{
                formId: formId,
                formSectionId: formSectionId,
                fieldId: `${formSectionId}-event-date-out`,
                required: assignedTo !== null && !allDay,
              }}
              format={GlobalConst.MOMENT_DATE_FORMAT_FULL}
              value={dateOut === null ? undefined : dayjs(dateOut)}
              onChange={(value) => setDateOut(value)}
              disabled={allDay}
            />
            <FormControlLabel
              sx={{ pt: 3, flex: 1 }}
              control={
                <Checkbox
                  checked={allDay}
                  onChange={(_, checked) => setAllDay(checked)}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label={t("field:label.allDay")}
            />
          </Stack>
          <FormInputText
            label={t("common:comment")}
            stackProps={{ sx: { flex: 1 } }}
            validationProps={{
              required: false,
              formId: formId,
              formSectionId: formSectionId,
              fieldId: `${formSectionId}-event-comment`,
            }}
            value={comment || ""}
            onChange={(event) => setComment(event.target.value)}
          />
          <Autocomplete
            multiple
            popupIcon={<ExpandMoreRoundedIcon />}
            options={resources}
            getOptionLabel={(option) => option.name}
            value={selectedResources}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(event, value) => setSelectedResources(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("common:resources")}
                placeholder={t("field:placeholder.selectResources")}
              />
            )}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
});

const SpentTimeInput = (props: {
  spentTime: string | null;
  setSpendTime: React.Dispatch<React.SetStateAction<string | null>>;
}) => {
  const { t } = useTranslation();
  const { spentTime, setSpendTime } = props;
  const [inputValue, setInputValue] = useState(spentTime || "");

  const handleOnChange = (value: string) => {
    const digits = value.replace(/\D/g, "");

    if (digits.length <= 4) {
      // Format hours and minutes based on the number of digits entered
      let formattedValue = "";
      if (digits.length >= 1) {
        formattedValue += digits[0];
      }
      if (digits.length >= 2) {
        formattedValue += digits[1];
        formattedValue += ":";
      }
      if (digits.length >= 3) {
        formattedValue += digits[2];
      }
      if (digits.length >= 4) {
        formattedValue += digits[3];
      }

      setInputValue(formattedValue);
    }

    if (digits.length === 4) setSpendTime(digits);
    else setSpendTime(null);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key == "Backspace") {
      if (!inputValue || !inputValue.length) return;
      event.preventDefault();
      const lastChar = inputValue.at(-1);
      if (lastChar === ":") setInputValue(inputValue.slice(0, -2));
      else setInputValue(inputValue.slice(0, -1));
    }
  };

  return (
    <Stack sx={{ flex: 1 }}>
      <InputLabel sx={{ mb: 1 }}>{t("field:label.spentTime")}</InputLabel>
      <OutlinedInput
        value={inputValue}
        onChange={(e) => handleOnChange(e.target.value)}
        onKeyDown={handleKeyPress}
        placeholder="hh:mm"
      />
    </Stack>
  );
};
